import React, { useEffect } from "react";

import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

import DataTable from "examples/Tables/DataTable";

import { useDispatch, useSelector } from "react-redux";

import CreateEditDevice from "../CreateEditDevice";

import DeleteModal from "../../../customComponents/devicesComponents/DeleteModal";

import { getDevice, deleteDevice } from "../../../api/devices";

import { setCurrentDevices, removeCurrentDivice } from "../../../redux/reducers/devicesReducer";

import { setCurrentOrder } from "../../../redux/reducers/ordersReducer";

import { getOrder } from "../../../api/orders";

import { canEditOrder } from "../../../utils/permissions";

export default function DeviceTable({ device, user }) {
  const currentDevices = useSelector((state) => state.devices.currentDevices);
  const currentOrder = useSelector((state) => state.orders.currentOrder);
  const canEdit = canEditOrder(user, currentOrder);

  const dispatch = useDispatch();

  const [deviceType, setDeviceType] = React.useState("");

  async function fetchOrder() {
    const response = await getOrder(currentOrder.order_id);
    dispatch(setCurrentOrder(response.data));
  }

  async function fetchDevice(deviceId) {
    const response = await getDevice(deviceId);
    return await response.data;
  }

  async function fetchDevices() {
    if (currentDevices?.length > 0) {
      const devices = [];
      for (let i = 0; i < currentDevices.length; i++) {
        devices.push(await fetchDevice(currentDevices[i].device_id));
      }

      // Sort the devices array based on the device identifier
      devices.sort((a, b) => {
        // Extract the base device identifier (ignoring anything after a hyphen)
        const baseIdA = a.device_number.split(" -")[0];
        const baseIdB = b.device_number.split(" -")[0];

        // Compare the base identifiers
        if (baseIdA < baseIdB) return -1;
        if (baseIdA > baseIdB) return 1;

        // If base identifiers are equal, compare the full device_id to maintain relative order
        return a.device_number.localeCompare(b.device_number);
      });

      dispatch(setCurrentDevices(devices));
    }
  }

  async function deleteDeviceAPI(deviceId) {
    await deleteDevice(deviceId);
  }

  async function submit() {
    await dispatch(removeCurrentDivice(device.device_id));
    await deleteDeviceAPI(device.device_id);
    await fetchOrder();
    await fetchDevices();
  }

  const allSuppliers = useSelector((state) => state.suppliers.allSuppliers);

  const supplier =
    allSuppliers.length > 0 && allSuppliers.find(({ id }) => id === device.supplier_id);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  useEffect(() => {
    if (device) {
      setDeviceType(device.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  const deviceColumns = [
    { Header: "Adresse précise", accessor: "adress", align: "center" },
    { Header: "Type d'appareil", accessor: "type", align: "center" },
    ...(device && !deviceType.includes("Port")
      ? [{ Header: "Charge utile", accessor: "payload", align: "center" }]
      : []),
    ...(device && !deviceType.includes("Port")
      ? [{ Header: "Nombre de niveau", accessor: "level_number", align: "center" }]
      : []),
    ...(device && !deviceType.includes("Port")
      ? [{ Header: "Nombre de face de service", accessor: "service_sides_number", align: "center" }]
      : []),
    ...(device && !deviceType.includes("Port") && deviceType !== "Chaise"
      ? [{ Header: "Avec pylône", accessor: "pylon", align: "center" }]
      : []),
    ...(device && deviceType === "Chaise"
      ? [{ Header: "Configuration", accessor: "configuration", align: "center" }]
      : []),
    ...(device && !deviceType.includes("Port")
      ? [{ Header: "Position", accessor: "interior", align: "center" }]
      : []),
    { Header: "Délai fabrication appareil", accessor: "wait", align: "center", info: "wait" },
    { Header: "Durée chantier", accessor: "period", align: "center", info: "period" },
  ];

  const deviceRows = [
    {
      adress: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.adress ? device.adress : "Non spécifiée"}
        </MDTypography>
      ),
      type: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.type ? device.type : "Non spécifiée"}
        </MDTypography>
      ),
      payload: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.payload ? `${device.payload} Kg` : "Non spécifiée"}
        </MDTypography>
      ),
      level_number: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.level_number ? device.level_number : "Non spécifiée"}
        </MDTypography>
      ),
      service_sides_number: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.faces_number ? device.faces_number : "Non spécifiée"}
        </MDTypography>
      ),
      pylon: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device.pylon !== null ? (
            <MDBox ml={-1}>
              <MDBadge
                badgeContent={device.pylon}
                color={device.pylon === "Sans" ? (!darkMode ? "dark" : "light") : "success"}
                variant="gradient"
                size="sm"
              />
            </MDBox>
          ) : (
            <MDTypography component="a" variant="button" fontWeight="medium">
              Non spécifiée
            </MDTypography>
          )}
        </MDTypography>
      ),
      configuration: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.configuration ? device.configuration : "Non spécifiée"}
        </MDTypography>
      ),
      interior: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.location ? device.location : "Non spécifiée"}
        </MDTypography>
      ),
      wait: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.wait ? device.wait + " semaines" : "Non spécifié"}
        </MDTypography>
      ),
      period: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device && device.period ? device.period + " semaines" : "Non spécifié"}
        </MDTypography>
      ),
    },
  ];

  return (
    <Grid item xs={12} mt={3} sx={{ height: "100%" }}>
      <Card sx={{ height: "100%" }}>
        <MDBox
          mx={2}
          mt={-3}
          py={1.5}
          px={2}
          variant="gradient"
          borderRadius="lg"
          sx={{
            background: !darkMode ? "linear-gradient(195deg, #364c70, #273956)" : "white",
            boxShadow: darkMode ? "0 1px 4px 1px white" : "0 1px 4px 1px #344767",
            display: "flex",
            verticalAlign: "middle",
          }}
        >
          <MDTypography
            variant="h5"
            style={{
              width: "50%",
              lineHeight: "2.4rem",
              display: "tableCell",
              verticalAlign: "middle",
            }}
            color={darkMode ? "inherit" : "white"}
          >
            Appareil {device.device_number}{" "}
            {device.ot_number !== null ? "- " + device.ot_number : ""}
          </MDTypography>
          {canEdit && (
              <Grid style={{ textAlign: "right", width: "50%" }}>
                <CreateEditDevice device={device} edit={true} renderStep={0} />
                <DeleteModal
                  message={`l'appareil ${device.device_number}`}
                  onOk={submit}
                  type="device"
                />
              </Grid>
            )}
        </MDBox>
        <MDBox
          pt={0.75}
          sx={{
            height: "100%",
            "div.MuiTableContainer-root": {
              height: "100%",
              display: "flex",
            },
          }}
        >
          <DataTable
            table={{ columns: deviceColumns, rows: deviceRows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      </Card>
    </Grid>
  );
}
